import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { App } from "@capacitor/app";
import { isNativeApp } from "./capacitor";
import { useAuth } from "./auth-provider";

interface CrashReport {
  timestamp: number;
  userId: string | null;
  error: string;
  context: string;
  userAgent: string;
  url: string;
  appState: "foreground" | "background" | "resumed";
  lastActiveTime: number | null;
  timeSinceLastActive: number | null;
}

interface ConnectionRecoveryContextType {
  isStale: boolean;
  isRecovering: boolean;
  lastActiveTime: number | null;
  timeSinceResume: number;
  showRecoveryModal: boolean;
  setShowRecoveryModal: (show: boolean) => void;
  handleRefresh: () => void;
  handleWaitLonger: () => void;
  handleSendReport: (additionalInfo?: string) => Promise<void>;
  reportConnectionIssue: (context: string) => void;
  registerLoadingState: (key: string, isLoading: boolean) => void;
  wasRecentlyResumed: boolean;
}

const ConnectionRecoveryContext = createContext<ConnectionRecoveryContextType | null>(null);

const STALE_THRESHOLD_MS = 8000; // 8 seconds of loading after resume = stale
const BACKGROUND_THRESHOLD_MS = 30000; // 30 seconds in background triggers monitoring
const RESUME_WINDOW_MS = 20000; // Consider "recently resumed" for 20 seconds
const AUTO_REFRESH_THRESHOLD_MS = 25000; // Auto-refresh if stale for 25+ seconds

export function ConnectionRecoveryProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [isStale, setIsStale] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [lastActiveTime, setLastActiveTime] = useState<number | null>(null);
  const [timeSinceResume, setTimeSinceResume] = useState(0);
  const [wasRecentlyResumed, setWasRecentlyResumed] = useState(false);

  const loadingStatesRef = useRef<Map<string, boolean>>(new Map());
  const resumeTimeRef = useRef<number | null>(null);
  const staleCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundStartRef = useRef<number | null>(null);
  const resumeWindowTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRefresh = useCallback(() => {
    setIsRecovering(true);
    window.location.reload();
  }, []);

  const handleWaitLonger = useCallback(() => {
    setShowRecoveryModal(false);
    setIsStale(false);
    resumeTimeRef.current = Date.now();

    if (staleCheckIntervalRef.current) {
      clearInterval(staleCheckIntervalRef.current);
    }

    startStaleCheck();
  }, []);

  const handleSendReport = useCallback(async (additionalInfo?: string) => {
    const loadingKeys = Array.from(loadingStatesRef.current.entries())
      .filter(([_, isLoading]) => isLoading)
      .map(([key]) => key);

    const report: CrashReport = {
      timestamp: Date.now(),
      userId: user?.id || null,
      error: "Connection stale after app resume",
      context: additionalInfo || `User triggered crash report. Loading keys: ${loadingKeys.join(", ") || "none"}`,
      userAgent: navigator.userAgent,
      url: window.location.href,
      appState: "resumed",
      lastActiveTime: lastActiveTime,
      timeSinceLastActive: lastActiveTime ? Date.now() - lastActiveTime : null,
    };

    try {
      const response = await fetch("/api/crash-reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(report),
      });

      if (!response.ok) {
        console.error("[ConnectionRecovery] Failed to send crash report:", response.status);
      }
    } catch (error) {
      console.error("[ConnectionRecovery] Error sending crash report:", error);
      try {
        const stored = localStorage.getItem("pending_crash_reports");
        const reports = stored ? JSON.parse(stored) : [];
        reports.push(report);
        localStorage.setItem("pending_crash_reports", JSON.stringify(reports.slice(-10)));
      } catch (e) {
        console.error("[ConnectionRecovery] Failed to store locally:", e);
      }
    }
  }, [user?.id, lastActiveTime]);

  const reportConnectionIssue = useCallback((context: string) => {
    console.log("[ConnectionRecovery] Connection issue reported:", context);
    handleSendReport(context);
  }, [handleSendReport]);

  const registerLoadingState = useCallback((key: string, isLoading: boolean) => {
    loadingStatesRef.current.set(key, isLoading);

    if (!isLoading && wasRecentlyResumed) {
      const anyStillLoading = Array.from(loadingStatesRef.current.values()).some(v => v);
      if (!anyStillLoading) {
        console.log("[ConnectionRecovery] All queries loaded after resume");
        setIsStale(false);
        if (staleCheckIntervalRef.current) {
          clearInterval(staleCheckIntervalRef.current);
          staleCheckIntervalRef.current = null;
        }
      }
    }
  }, [wasRecentlyResumed]);

  const startStaleCheck = useCallback(() => {
    if (staleCheckIntervalRef.current) {
      clearInterval(staleCheckIntervalRef.current);
    }

    staleCheckIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - (resumeTimeRef.current || Date.now());
      setTimeSinceResume(elapsed);

      const anyLoading = Array.from(loadingStatesRef.current.values()).some(v => v);

      if (anyLoading && elapsed > STALE_THRESHOLD_MS) {
        setIsStale(true);

        if (elapsed > AUTO_REFRESH_THRESHOLD_MS) {
          if (staleCheckIntervalRef.current) {
            clearInterval(staleCheckIntervalRef.current);
            staleCheckIntervalRef.current = null;
          }
          setIsRecovering(true);
          window.location.reload();
          return;
        }

        setShowRecoveryModal(true);
      } else if (!anyLoading) {
        setIsStale(false);

        if (staleCheckIntervalRef.current) {
          clearInterval(staleCheckIntervalRef.current);
          staleCheckIntervalRef.current = null;
        }
      }
    }, 1000);
  }, []);

  const handleAppResume = useCallback(async (wasBackgroundedFor: number) => {
    if (wasBackgroundedFor < BACKGROUND_THRESHOLD_MS) {
      return;
    }

    console.log("[ConnectionRecovery] Long background period detected:", wasBackgroundedFor, "ms");

    resumeTimeRef.current = Date.now();
    setTimeSinceResume(0);
    setWasRecentlyResumed(true);

    if (resumeWindowTimeoutRef.current) {
      clearTimeout(resumeWindowTimeoutRef.current);
    }

    resumeWindowTimeoutRef.current = setTimeout(() => {
      setWasRecentlyResumed(false);
    }, RESUME_WINDOW_MS);

    startStaleCheck();
  }, [startStaleCheck]);

  // Handle app state changes for native apps
  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }

    const handleAppStateChange = async (state: { isActive: boolean }) => {
      if (!state.isActive) {
        backgroundStartRef.current = Date.now();
        setLastActiveTime(Date.now());
        return;
      }

      const now = Date.now();
      const wasBackgroundedFor = backgroundStartRef.current
        ? now - backgroundStartRef.current
        : 0;

      backgroundStartRef.current = null;
      handleAppResume(wasBackgroundedFor);
    };

    let handle: { remove: () => void } | null = null;

    App.addListener("appStateChange", handleAppStateChange)
      .then((h) => {
        handle = h;
      })
      .catch((e) => {
        console.warn("[ConnectionRecovery] Failed to add listener:", e);
      });

    return () => {
      if (handle) {
        handle.remove();
      }
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
      }
      if (resumeWindowTimeoutRef.current) {
        clearTimeout(resumeWindowTimeoutRef.current);
      }
    };
  }, [handleAppResume]);

  // Handle visibility change for web
  useEffect(() => {
    if (isNativeApp()) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        backgroundStartRef.current = Date.now();
        setLastActiveTime(Date.now());
      } else {
        const now = Date.now();
        const wasHiddenFor = backgroundStartRef.current
          ? now - backgroundStartRef.current
          : 0;

        backgroundStartRef.current = null;
        handleAppResume(wasHiddenFor);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
      }
      if (resumeWindowTimeoutRef.current) {
        clearTimeout(resumeWindowTimeoutRef.current);
      }
    };
  }, [handleAppResume]);

  return (
    <ConnectionRecoveryContext.Provider
      value={{
        isStale,
        isRecovering,
        lastActiveTime,
        timeSinceResume,
        showRecoveryModal,
        setShowRecoveryModal,
        handleRefresh,
        handleWaitLonger,
        handleSendReport,
        reportConnectionIssue,
        registerLoadingState,
        wasRecentlyResumed,
      }}
    >
      {children}
    </ConnectionRecoveryContext.Provider>
  );
}

export function useConnectionRecovery() {
  const context = useContext(ConnectionRecoveryContext);
  if (!context) {
    throw new Error("useConnectionRecovery must be used within ConnectionRecoveryProvider");
  }
  return context;
}

export function useConnectionRecoveryOptional() {
  return useContext(ConnectionRecoveryContext);
}

// Hook for providers to register their loading state
export function useRegisterLoadingState(key: string, isLoading: boolean) {
  const recovery = useConnectionRecoveryOptional();

  useEffect(() => {
    if (recovery) {
      recovery.registerLoadingState(key, isLoading);
    }
  }, [recovery, key, isLoading]);
}
